import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Box } from '@mui/material';
import { FieldText } from './fields/FieldText';
import { FieldSelect } from './fields/FieldSelect';

/** US states + DC (postal abbreviations). */
export const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
];

export interface ShippingAddressLabels {
  fullName: string;
  fullNameHint?: string;
  /** Placeholder clarifying what Attn is for (e.g. "Recipient name or department"). */
  fullNamePlaceholder?: string;
  street1: string;
  street2: string;
  street2Hint?: string;
  city: string;
  state: string;
  statePlaceholder?: string;
  postalCode: string;
}

export interface ShippingAddressFieldsProps<T extends FieldValues> {
  control: Control<T>;
  /** The RHF object path holding the address (e.g. `"shippingAddress"`). */
  namePrefix: string;
  labels: ShippingAddressLabels;
  states?: string[];
  disabled?: boolean;
}

/**
 * US shipping-address group (the Polaris kit `ShippingAddressFields`) — Attn,
 * Address 1/2, City, a State dropdown, and ZIP — bound onto an RHF object at
 * `namePrefix`. Maps to the wire `ShippingAddress`
 * (`fullName`/`street1`/`street2`/`city`/`state`/`postalCode`); the form sets
 * `country` (US-only at MVP) so it needs no field here.
 *
 * Attn and Address 2 are optional supplementary routing lines — Attn takes a
 * placeholder ("Recipient name or department") because the shipment's actual
 * recipient is the request's requested-for user, not this field (see BUG-009).
 * They are consequently the only two fields here without a required marker; the
 * other four mirror what `makeDeviceAcquisitionSchema` enforces.
 */
export function ShippingAddressFields<T extends FieldValues>({
  control,
  namePrefix,
  labels,
  states = US_STATES,
  disabled,
}: ShippingAddressFieldsProps<T>) {
  const path = (key: string) => `${namePrefix}.${key}` as FieldPath<T>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FieldText
        control={control}
        name={path('fullName')}
        label={labels.fullName}
        helperText={labels.fullNameHint}
        placeholder={labels.fullNamePlaceholder}
        disabled={disabled}
      />
      <FieldText
        control={control}
        name={path('street1')}
        label={labels.street1}
        required
        disabled={disabled}
      />
      <FieldText
        control={control}
        name={path('street2')}
        label={labels.street2}
        helperText={labels.street2Hint}
        disabled={disabled}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)',
          gap: 1.75,
        }}
      >
        <FieldText
          control={control}
          name={path('city')}
          label={labels.city}
          required
          disabled={disabled}
        />
        <FieldSelect
          control={control}
          name={path('state')}
          label={labels.state}
          placeholder={labels.statePlaceholder}
          required
          disabled={disabled}
          options={states.map((s) => ({ value: s, label: s }))}
        />
        <FieldText
          control={control}
          name={path('postalCode')}
          label={labels.postalCode}
          required
          disabled={disabled}
        />
      </Box>
    </Box>
  );
}
